import React from 'react';
import { Section, Container, SectionHeader } from '@layout';
import PortfolioNav from '../PortfolioNav';
import PortfolioTab from '../PortfolioTab';
import type { PortfolioSection } from '@lib/portfolioSections';
import { sectionSlug } from '@lib/portfolioSections';
import { buildPortfolioSectionBreadcrumbs, breadcrumbAriaLabel } from '@lib/portfolioBreadcrumbs';
import { Breadcrumb, MainHero } from '@ui';

export interface PortfolioSectionWrapperProps {
  selected: PortfolioSection;
  portfolioSections: PortfolioSection[];
  locale?: 'en' | 'es';
  /** English default site uses `/portfolio` without `/en`; `[lang]` routes set `false`. */
  prefixlessEnglish?: boolean;
  /** Passed to {@link Section} as `sectionID`; defaults to `portfolio-{sectionSlug}`. */
  sectionID?: string;
  className?: string;
}

/**
 * Layout shell for a single portfolio section index (`/portfolio/[section]`), mirroring
 * {@link PortfolioWrapper} for the main portfolio page: hero, breadcrumb, tab grid, section nav.
 */
const PortfolioSectionWrapper: React.FC<PortfolioSectionWrapperProps> = ({
  selected,
  portfolioSections,
  locale = 'en',
  prefixlessEnglish = true,
  sectionID,
  className = 'main-section portfolio',
}) => {
  const slug = sectionSlug(selected);
  const breadcrumbOpts = { locale, prefixlessEnglish };

  return (
    <>
      <MainHero
        className='portfolio-hero'
        main={
          <>
            <Breadcrumb
              ariaLabel={breadcrumbAriaLabel(locale)}
              items={buildPortfolioSectionBreadcrumbs(breadcrumbOpts, selected.title, slug)}
            />
            <SectionHeader
              title={selected.title}
              description={selected.description ?? ''}
              as='h1'
              descriptionClassName={selected.description ? 'sub' : undefined}
            />
          </>
        }
      />
      <div className='section-group'>
        <Section sectionID={sectionID || `portfolio-${slug}`} className={className}>
          <Container>
            <PortfolioTab
              title=''
              description=''
              locale={locale}
              sectionSlug={slug}
              portfolioImages={selected.portfolio_images ?? []}
            />
            <PortfolioNav
              portfolioSections={portfolioSections}
              locale={locale}
              headingType='h3'
              headingClass='portfolio-section-nav-heading'
            />
          </Container>
        </Section>
      </div>
    </>
  );
};

export default PortfolioSectionWrapper;
